import React, { useCallback, useEffect, useState } from 'react';
import c from 'classnames';

import { emitter, EventsEnum } from '@lyearn/events';
import { useTranslation } from '@lyearn/i18n';
import { ImagePlaceholder, MoreVertical } from '@lyearn/icons';
import { useReactNodeView } from '@lyearn/lygo-core';
import { Box, Image, Input, Skeleton, Stack, Typography } from '@lyearn/ui';

import ResizeContainer from '../../../components/ResizeContainer/ResizeContainer';
import { allTransition } from '../../../components/ResizeContainer/style';
import { UploadButton } from '../../../components/UploadButton';
import { UploadCard } from '../../../components/UploadCard';
import useBase64Media from '../../../hooks/useBase64Media';
import useDrop from '../../../hooks/useDrop';
import { usePortalForForms } from '../../../hooks/usePortalForForms';
import useRemoteUpload from '../../../hooks/useRemoteUpload';
import useUpload, { FileType } from '../../../hooks/useUpload';
import useUploadRemoteMedia from '../../../hooks/useUploadRemoteMedia';
import { ImageComponentProps, ImageLayoutTypes } from '../types';
import BlockTitleAndDescription from './blockTitleAndDescription';
import { imageLayoutStyles, imageStyles } from './styles';
import VideoIcon from './videoSvg';
// import VideoIcon from 'nodes/Video/plugins/videoSvg';

const getSrcSetUrls = (srcset: string) =>
  srcset?.split(/,\s+/).map((v?: string) => v?.split(' ')[0] ?? '');

const renderFullWidthImage = (alt?: string, src?: string) => (
  <div className={c(allTransition, 'my-8 w-full')}>
    <img alt={alt} className="h-full object-cover" src={src} />
  </div>
);

const ImageComponent: React.FC<ImageComponentProps> = (props) => {
  const {
    showFullWidth = false,
    src,
    srcset,
    alt,
    width,
    id,
    caption,
    height,
    maxHeight,
    layout = ImageLayoutTypes.Card,
    hotspots,
    hotspotsEnabled,
    className,
    isUploadingExternally = false,
    disableResizing = false,
  } = props;

  const imgRef = React.useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const { node, view, updateAttrs, isMicroLearning = false } = useReactNodeView();

  const updateEditorState = useCallback(
    (file: FileType) => {
      updateAttrs({
        src: file.url,
        alt: file?.name,
      });
    },
    [updateAttrs],
  );

  const { uppy, currentFile } = useUpload(updateEditorState, node?.attrs.id);
  useDrop(node, updateAttrs, uppy);
  useBase64Media(uppy, currentFile, view?.editable ? node : undefined);
  useUploadRemoteMedia(uppy, currentFile, view?.editable ? node : undefined);

  const onChangeSrcSet = useCallback(
    (oldUrl: string, newUrl: string) => {
      updateAttrs({ srcset: node?.attrs.srcset.replace(new RegExp(oldUrl, 'g'), newUrl) });
    },
    [node?.attrs.srcset, updateAttrs],
  );

  const { addToUpload: onSrcSetUpload } = useRemoteUpload(uppy, onChangeSrcSet);

  useEffect(() => {
    const srcSetUrls = getSrcSetUrls(node?.attrs.srcset);
    onSrcSetUpload(srcSetUrls);
  }, [onSrcSetUpload, node?.attrs.srcset]);

  const isUploading = (!!currentFile && !currentFile.url) || isUploadingExternally;

  const imageAttributesFrom = usePortalForForms(
    imgRef,
    <Stack className="" direction="col" gapY={10}>
      <BlockTitleAndDescription />
    </Stack>,
    !isMicroLearning,
  );

  // gen ai
  const generativeTaskId = node?.attrs?.generativeTaskId;
  const generationStatus = node?.attrs?.generationStatus;

  if (!generationStatus && generativeTaskId) {
    emitter.emit(EventsEnum.ImageGeneration, {
      node,
      updateAttrs,
    });

    return (
      <Skeleton
        className="mx-auto my-32 rounded-16"
        height={400}
        variant="rectangular"
        width={400}
      />
    );
  }

  return (
    <>
      {src ? (
        showFullWidth ? (
          renderFullWidthImage(alt, src)
        ) : isMicroLearning ? (
          <Stack
            className={c(
              'bg-bg-hover-light  mt-24 mb-24 w-full h-fit w-full rounded-16  border border-border',
            )}
            direction="col"
            onClick={(e) => e.stopPropagation()}>
            <Stack
              alignItems="center"
              className="h-[6.2rem] p-12 border-b border-border"
              justifyContent="between">
              <Stack alignItems="center" className="" gapX={12}>
                <VideoIcon />
                <Stack direction="col">
                  <Typography numberOfLines={1} variant="body-short-02">
                    {node?.attrs?.alt ?? 'Unnamed.mov'}
                  </Typography>
                  <Typography className="text-secondary" variant="body-short-03">
                    Preview
                  </Typography>
                </Stack>
              </Stack>
              {/* <MoreVertical className="h-24 w-24" fillColorToken="icon-secondary" /> */}
            </Stack>

            <Stack className="w-full p-24 " direction="col" gapY={24}>
              <Image
                alt={alt}
                className="h-full max-h-[40rem] rounded-12 object-cover"
                src={src}
                srcSet={srcset}
              />

              <Stack ref={imgRef} className="h-[11rem] " direction="col" gapY={10}>
                {imageAttributesFrom}
              </Stack>
            </Stack>
          </Stack>
        ) : (
          <ResizeContainer
            canChangeHeight={hotspotsEnabled ? false : true}
            caption={caption}
            className={className}
            disableResizing={disableResizing}
            hotspots={hotspots}
            hotspotsEnabled={hotspotsEnabled}
            isMicroLearning={isMicroLearning}
            maxHeight={maxHeight}
            nodeRef={imgRef}
            readOnly={false}
            width={width as number}>
            {({ calculatedWidth }: { calculatedWidth: number }) => (
              <div
                ref={imgRef}
                className={c(
                  allTransition,
                  imageStyles,
                  imageLayoutStyles(layout),
                  ImageLayoutTypes.Web === layout ? 'image-web-container' : '',
                )}
                style={{ height: height && `${height}px`, width: `${calculatedWidth}px` }}>
                {ImageLayoutTypes.Web === layout ? (
                  <Box className="flex h-24 w-full p-8">
                    {[0, 1, 2].map((value) => (
                      <Box
                        key={value}
                        className={c(
                          'h-8 w-8 rounded-8 bg-interactive-secondary',
                          value ? 'ms-4' : '',
                        )}
                      />
                    ))}
                  </Box>
                ) : null}
                <img alt={alt} className="h-full object-cover" src={src} srcSet={srcset} />
              </div>
            )}
          </ResizeContainer>
        )
      ) : isUploading ? (
        <div className="mt-16">
          <UploadCard
            isUploading
            PlaceholderIcon={ImagePlaceholder}
            isUploadingExternally={isUploadingExternally}
            name={currentFile?.name}
            progress={currentFile?.fileProgress as number}
          />
        </div>
      ) : (
        <UploadButton
          PlaceholderIcon={ImagePlaceholder}
          id={id as string}
          placeHolder={hotspotsEnabled ? t('Lygo:HotspotButtonPlaceholder') : t('Lygo:UploadImage')}
          type={'image/*'}
        />
      )}
    </>
  );
};

export default ImageComponent;
